import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { IDS } from '@/constants/testIds';

const NAV = [
  { label: 'Праздничные туры', to: '/#festival' },
  { label: 'Еженедельные', to: '/#weekly' },
  { label: 'О гиде', to: '/#about-ali' },
  { label: 'Контакты', to: '/#contacts' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (to) => (e) => {
    setOpen(false);
    const [path, hash] = to.split('#');
    if (loc.pathname !== (path || '/')) {
      e.preventDefault();
      navigate(path || '/');
      setTimeout(() => {
        const el = hash && document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? 'backdrop-blur-md bg-background/70 border-b border-border/60' : 'bg-transparent'
        }`}
      >
        <div className="container-x flex items-center justify-between py-5 md:py-6">
          <Link
            to="/"
            data-testid={IDS.navLogo}
            className="group flex items-center gap-3"
          >
            <span className="text-2xl font-serif-display italic font-medium tracking-tight text-foreground">
              Ali<span className="text-saffron">.</span>India
            </span>
          </Link>

          <nav data-testid={IDS.navMenu} className="hidden lg:flex items-center gap-9 text-sm font-medium">
            {NAV.map((n) => (
              <a
                key={n.to}
                href={n.to}
                onClick={handleAnchor(n.to)}
                className="hover-underline text-foreground/85 hover:text-foreground transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid={IDS.navBookBtn}
              href="/#festival"
              onClick={handleAnchor('/#festival')}
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest2 font-bold border border-saffron text-saffron px-5 py-3 hover:bg-saffron hover:text-foreground transition-colors"
            >
              Забронировать
            </a>
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 text-foreground"
              aria-label="Меню"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-background/95 backdrop-blur-md lg:hidden pt-24"
          >
            <nav className="container-x flex flex-col gap-6 text-3xl font-serif-display">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  href={n.to}
                  onClick={handleAnchor(n.to)}
                  className="text-foreground hover:text-saffron"
                >
                  {n.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
